// photoQueueService.js
// [Added by E2logy]: Queue-first upload of survey photos.
//
// Every photo taken in the survey is written to IndexedDB first and uploaded by
// a single background worker afterwards. The surveyor never waits for the
// network, which is what makes a store with a weak or missing connection usable.
//
// Guarantees:
//  - the record is written before the caller continues, so a photo is never lost
//    because the user navigated away too quickly
//  - only one uploader runs at a time (single flight), so a slow upload is never
//    started again by the next tick, which used to create duplicate photos.
//    [Modified by E2logy]: this now holds across all open tabs of the app too
//  - the uploaded photo id is persisted before the survey answer is saved, so a
//    retry re-uses the same image instead of uploading it a second time
//  - failures back off, are capped, and stay visible to the user
//  - [Modified by E2logy]: a photo is never given up on. Requests have a
//    timeout, network trouble does not use up the attempts, and a photo the
//    server rejected many times is still retried on a long delay
//  - [Added by E2logy]: "Send all" sends the photos in batches of BATCH_SIZE
//    with a pause between batches, so many photos at once do not load the
//    server. The normal background upload sends them one by one, no pause
//  - [Added by E2logy]: an expired login (401) logs the user out like any other
//    API call. The photos stay on the device and continue after the next login
//  - [Added by E2logy]: a watchdog releases a run that stopped making progress,
//    so the queue never needs a page reload to start again

import Vue from "vue";
import ApiService from "./apiService";
import store from "../store";
import {
  putPendingPhoto,
  getPendingPhoto,
  getAllPendingPhotos,
  removePendingPhoto,
  countPendingPhotos,
  putPendingPortal,
  getPendingPortal,
  getAllPendingPortals,
  removePendingPortal
} from "./indexedDB";

export const PHOTO_UPLOAD_STATUS = {
  PENDING: "pending",
  UPLOADING: "uploading",
  FAILED: "failed"
};

const MAX_ATTEMPTS = 10;
const BASE_BACKOFF_MS = 5000;
const MAX_BACKOFF_MS = 3 * 60 * 1000;
const DRAIN_INTERVAL_MS = 15000;
// A photo that used all its attempts is still retried, just rarely.
const FAILED_RETRY_MS = 30 * 60 * 1000;
// Wait after a network error. It does not use up an attempt.
const NETWORK_RETRY_MS = 30 * 1000;
// The ApiService wrappers have no timeout and never settle on 401/403, so the
// queue calls axios directly with a timeout. One stuck request used to block
// every photo behind it.
// A photo upload has no total time limit, because on a weak store network a
// big photo can need several minutes. It is only stopped when no data moved
// for this long (a dead connection), and then tried again later.
const UPLOAD_STALL_TIMEOUT_MS = 90 * 1000;
const SAVE_TIMEOUT_MS = 45 * 1000;
// "Send all" sends BATCH_SIZE photos one by one, then waits BATCH_PAUSE_MS
// before the next batch. The normal background upload does not pause.
const BATCH_SIZE = 5;
const BATCH_PAUSE_MS = 5000;
// A run that shows no progress for this long is stuck (for example on a
// browser call that never answers), and the watchdog releases it. It is
// longer than the longest single step: an upload stall or a save timeout.
const RUN_STALL_LIMIT_MS = 3 * 60 * 1000;
// All tabs of the app share the same IndexedDB, and each tab runs this worker.
// Without a lock two tabs uploaded the same photo at the same time, and the
// server stored it twice with two different photo ids. The browser releases
// the lock by itself when the tab that holds it is closed.
const QUEUE_LOCK_NAME = "gennera_photo_queue";
const withQueueLock = (task, options = {}) => {
  if (typeof navigator === "undefined" || !navigator.locks) return task(true);
  return navigator.locks.request(QUEUE_LOCK_NAME, options, lock =>
    task(!!lock)
  );
};

// Reactive view of the queue so components can render a per photo status
// without reading IndexedDB on every re-render.
const state = Vue.observable({
  statuses: {},
  byQuestion: {},
  portalByQuestion: {},
  pendingCount: 0,
  failedCount: 0,
  pendingPortalCount: 0,
  // Progress of flushAll(), shown by the finish route dialog.
  flush: { isRunning: false, total: 0, sent: 0 }
});

/**
 * Identity of a survey photo question. Used so a Photo component can find its
 * own queued photos without any extra props being passed down the tree.
 */
export const buildQuestionKey = ({
  worksessionPosBrandId,
  surveyId,
  componentId,
  surveyProOrMatId,
  surveyQuestionId
}) =>
  [
    worksessionPosBrandId || "",
    surveyId || "",
    componentId || "",
    surveyProOrMatId || "",
    surveyQuestionId || ""
  ].join("_");

// A rating rides along with its own photo, so while that photo is still
// queued and still being attempted, the rating is not a second pending
// upload. A photo that has permanently FAILED is no longer being attempted
// (it waits for an explicit user retry), so its rating must count on its
// own again - otherwise it would stay invisible for as long as the photo
// stays failed.
const questionHasPendingPhoto = key =>
  (state.byQuestion[key] || []).some(
    entry => entry.status !== PHOTO_UPLOAD_STATUS.FAILED
  );

let isDraining = false;
let drainPromise = null;
let flushPromise = null;
let drainIntervalId = null;
let isStarted = false;
const syncedListeners = new Set();
const portalSyncedListeners = new Set();

// One preview URL per queued photo. Created once from the stored blob so the
// thumbnail keeps showing even when the survey data is reloaded, and released as
// soon as the photo leaves the queue.
const previewUrls = new Map();

const getPreviewUrl = record => {
  if (!record || !record.blob) return null;
  if (previewUrls.has(record.uuid)) return previewUrls.get(record.uuid);
  try {
    const url = URL.createObjectURL(record.blob);
    previewUrls.set(record.uuid, url);
    return url;
  } catch (error) {
    console.log("photo preview url error - ", error);
    return null;
  }
};

const releasePreviewUrls = keepUuids => {
  previewUrls.forEach((url, uuid) => {
    if (keepUuids.has(uuid)) return;
    try {
      URL.revokeObjectURL(url);
    } catch (error) {
      // nothing to do, the url is going away anyway
    }
    previewUrls.delete(uuid);
  });
};

const createUuid = () => {
  if (window.crypto && typeof window.crypto.randomUUID === "function") {
    return window.crypto.randomUUID();
  }
  return `photo_${Date.now()}_${Math.random()
    .toString(36)
    .slice(2, 10)}`;
};

const isOfflineNow = () =>
  typeof navigator !== "undefined" && navigator.onLine === false;

const describeError = error => {
  if (!error) return "unknown error";
  if (typeof error === "string") return error;
  if (error.response?.data?.message) return String(error.response.data.message);
  if (error.response?.status) return `HTTP ${error.response.status}`;
  if (error.code === "ECONNABORTED") return "timeout";
  if (error.message) return error.message;
  return "unknown error";
};

const backoffFor = attempts => {
  const delay = BASE_BACKOFF_MS * Math.pow(2, Math.max(0, attempts - 1));
  return Math.min(delay, MAX_BACKOFF_MS);
};

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

/**
 * [Added by E2logy]: how a failed request is handled.
 *  - NETWORK: no connection, a timeout or a server that is briefly down. Not
 *    the photo's fault, so no attempt is used and the run stops for now.
 *  - LOGIN: the login expired (401). The user is logged out and the photo
 *    waits on the device for the next login.
 *  - PHOTO: the server refused this one item. It backs off and the run goes
 *    on with the next item, so one bad photo never blocks the others.
 */
const FAILURE_KIND = {
  NETWORK: "network",
  LOGIN: "login",
  PHOTO: "photo"
};

const failureKindOf = error => {
  const status = error?.response?.status;
  if (status === 401) return FAILURE_KIND.LOGIN;
  if ([502, 503, 504].includes(status)) return FAILURE_KIND.NETWORK;
  // A cancelled request comes from our own stall timer. An axios error with
  // no response is a dropped connection or a timeout.
  if (Vue.axios.isCancel(error)) return FAILURE_KIND.NETWORK;
  if (error?.isAxiosError && !error.response) return FAILURE_KIND.NETWORK;
  return FAILURE_KIND.PHOTO;
};

const isLoggedIn = () =>
  !!store.getters["auth/isAuthenticated"] && !!ApiService.getToken();

// [Added by E2logy]: an expired login is handled like everywhere else in the
// app: the user is sent to the login page. The photos stay on the device and
// the queue starts again after the login (see App.vue). A 401 answered to an
// older token, sent just before a new login, is ignored.
const onLoginExpired = tokenUsed => {
  if (tokenUsed !== ApiService.getToken()) return;
  if (!store.getters["auth/isAuthenticated"]) return;
  store.dispatch("auth/logout");
};

/****** [Added by E2logy]: run tracking for the watchdog and "Send all" ******/

// The run that holds the queue right now, or null.
let activeRun = null;
// Items being sent right now. A run released by the watchdog may still finish
// its current item later, so a new run skips these to never send one twice.
const busyUuids = new Set();

const sendOnce = async (uuid, send) => {
  busyUuids.add(uuid);
  try {
    return await send();
  } finally {
    busyUuids.delete(uuid);
  }
};

const createRun = ({ isBatched }) => {
  let resolveReleased;
  const run = {
    isActive: true,
    isBatched,
    sentInBatch: 0,
    lastProgressAt: Date.now(),
    // Resolves when the watchdog gives up on this run.
    released: new Promise(resolve => {
      resolveReleased = resolve;
    }),
    release: null
  };
  run.release = () => {
    run.isActive = false;
    resolveReleased();
  };
  return run;
};

// Every step of a run (a request, upload progress, a pause) reports here.
const markProgress = () => {
  if (activeRun) activeRun.lastProgressAt = Date.now();
};

// Called on every queue tick. A run that made no progress for too long is
// stuck on a call that never answers. Releasing it frees the lock, so the next
// tick starts a fresh run without a page reload.
const releaseStalledRun = () => {
  if (!activeRun) return;
  if (Date.now() - activeRun.lastProgressAt < RUN_STALL_LIMIT_MS) return;
  console.log("photo queue run stalled, released by the watchdog");
  activeRun.release();
};

// "Send all" only: after BATCH_SIZE items in a row, pause before the next one.
const waitForBatchSlot = async run => {
  if (!run.isBatched) return;
  if (run.sentInBatch >= BATCH_SIZE) {
    await sleep(BATCH_PAUSE_MS);
    markProgress();
    run.sentInBatch = 0;
  }
  run.sentInBatch += 1;
};

const emitTo = (listeners, payload) => {
  listeners.forEach(listener => {
    try {
      listener(payload);
    } catch (error) {
      console.log("photo queue listener error - ", error);
    }
  });
};

const emitSynced = payload => emitTo(syncedListeners, payload);
const emitPortalSynced = payload => emitTo(portalSyncedListeners, payload);

const recordToFile = record => {
  const blob = record.blob;
  const mimeType = record.mimeType || (blob && blob.type) || "image/jpeg";
  const fileName = record.fileName || "photo.jpg";
  if (typeof File === "function") {
    try {
      return new File([blob], fileName, { type: mimeType });
    } catch (error) {
      // Some older WebKit builds cannot construct a File, the Blob still works.
    }
  }
  return blob;
};

// Rebuilds the whole reactive view from IndexedDB. IndexedDB is the source of
// truth, so this keeps the UI honest even after a page refresh.
const readStateFromStorage = async () => {
  try {
    const records = await getAllPendingPhotos();
    const nextByQuestion = {};
    const nextStatuses = {};
    const liveUuids = new Set();
    records.forEach(record => {
      const status = record.status || PHOTO_UPLOAD_STATUS.PENDING;
      liveUuids.add(record.uuid);
      const entry = {
        uuid: record.uuid,
        status,
        attempts: record.attempts || 0,
        lastError: record.lastError || null,
        createdAt: record.createdAt || 0,
        nextAttemptAt: record.nextAttemptAt || 0,
        fileName: record.fileName || null,
        userId: record.userId || null,
        worksessionPosBrandId: record.worksessionPosBrandId || null,
        surveyId: record.surveyId || null,
        surveyQuestionId: record.surveyQuestionId || null,
        // Names saved when the photo was taken, for the pending photos screen.
        // Photos queued by an older build have none.
        details: record.details || null,
        // The preview keeps the photo on screen while it waits to be sent.
        previewUrl: getPreviewUrl(record)
      };
      nextStatuses[record.uuid] = entry;
      const key = buildQuestionKey({
        worksessionPosBrandId: record.worksessionPosBrandId,
        surveyId: record.surveyId,
        componentId: record.componentId,
        surveyProOrMatId: record.surveyProOrMatId,
        surveyQuestionId: record.surveyQuestionId
      });
      if (!nextByQuestion[key]) nextByQuestion[key] = [];
      nextByQuestion[key].push(entry);
    });
    releasePreviewUrls(liveUuids);
    state.statuses = nextStatuses;
    state.byQuestion = nextByQuestion;
    state.pendingCount = records.length;
    state.failedCount = records.filter(
      el => el.status === PHOTO_UPLOAD_STATUS.FAILED
    ).length;
  } catch (error) {
    state.byQuestion = {};
    try {
      state.pendingCount = await countPendingPhotos();
    } catch (innerError) {
      console.log("photo queue count error - ", innerError);
    }
  }

  try {
    const portals = await getAllPendingPortals();
    const nextPortalByQuestion = {};
    let standalonePortalCount = 0;
    portals.forEach(record => {
      const key = buildQuestionKey({
        worksessionPosBrandId: record.worksessionPosBrandId,
        surveyId: record.surveyId,
        componentId: record.componentId,
        surveyProOrMatId: record.surveyProOrMatId,
        surveyQuestionId: record.surveyQuestionId
      });
      if (!nextPortalByQuestion[key]) nextPortalByQuestion[key] = [];
      nextPortalByQuestion[key].push({
        uuid: record.uuid,
        status: record.status || PHOTO_UPLOAD_STATUS.PENDING,
        attempts: record.attempts || 0,
        lastError: record.lastError || null,
        createdAt: record.createdAt || 0,
        scoreGPV: record.body?.portalComments?.scoreGPV ?? null,
        commentsGPV: record.body?.portalComments?.commentsGPV ?? null
      });
      if (!questionHasPendingPhoto(key)) standalonePortalCount += 1;
    });
    state.portalByQuestion = nextPortalByQuestion;
    state.pendingPortalCount = standalonePortalCount;
  } catch (error) {
    console.log("portal queue count error - ", error);
  }

  if (state.flush.isRunning) {
    const remaining = state.pendingCount + state.pendingPortalCount;
    state.flush.sent = Math.max(0, state.flush.total - remaining);
  }
};

// [Added by E2logy]: reading the whole queue is costly when many photos wait,
// and many callers ask for it at the same moment. Only one read runs at a
// time. A call that comes in during a read waits for one more read, so it
// still sees its own changes, and all such calls share that next read.
let refreshInProgress = null;
let refreshQueued = null;

const refreshState = () => {
  if (!refreshInProgress) {
    refreshInProgress = readStateFromStorage().finally(() => {
      refreshInProgress = null;
    });
    return refreshInProgress;
  }
  if (!refreshQueued) {
    refreshQueued = refreshInProgress.then(() => {
      refreshQueued = null;
      return refreshState();
    });
  }
  return refreshQueued;
};

const buildDeleteBody = record => ({
  worksessionPosBrandId: record.body?.worksessionPosBrandId,
  surveyId: record.body?.surveyId,
  componentId: record.body?.componentId,
  surveyProOrMatId: record.body?.surveyProOrMatId,
  surveyQuestionId: record.body?.surveyQuestionId,
  worksessionSurveyData: record.body?.worksessionSurveyData
});

const recordQuestionKey = record =>
  buildQuestionKey({
    worksessionPosBrandId: record.worksessionPosBrandId,
    surveyId: record.surveyId,
    componentId: record.componentId,
    surveyProOrMatId: record.surveyProOrMatId,
    surveyQuestionId: record.surveyQuestionId
  });

/**
 * Sends one queued GPV rating. savephotosportal finds the answer by question
 * criteria, so no server id is needed, but the photo answer has to exist first.
 * That is why the caller skips a rating whose photo is still queued.
 * @returns {Promise<{isSaved: boolean, failureKind: string|null}>}
 */
const uploadOnePortal = async record => {
  record.attempts = (record.attempts || 0) + 1;
  record.status = PHOTO_UPLOAD_STATUS.UPLOADING;
  await putPendingPortal(record);
  await refreshState();
  // The token this request is sent with, to tell an expired login apart from
  // a login that was renewed in the meantime.
  const tokenUsed = ApiService.getToken();
  markProgress();

  try {
    ApiService.initialize();
    const { data: response } = await Vue.axios.post(
      `worksession/savephotosportal`,
      // The survey completion in the body is from when the rating was typed,
      // the flag tells the backend not to overwrite a newer one.
      { ...record.body, isQueuedPhotoUpload: true },
      { timeout: SAVE_TIMEOUT_MS }
    );
    markProgress();
    await removePendingPortal(record.uuid);
    await refreshState();
    emitPortalSynced({ record, response });
    return { isSaved: true, failureKind: null };
  } catch (error) {
    markProgress();
    const failureKind = failureKindOf(error);
    // A rating is a small payload and must not be dropped, so it stays pending
    // and keeps retrying with the capped backoff instead of ending as failed.
    record.status = PHOTO_UPLOAD_STATUS.PENDING;
    record.lastError = describeError(error);
    if (failureKind === FAILURE_KIND.LOGIN) {
      // Sent again right after the next login.
      record.nextAttemptAt = 0;
    } else if (failureKind === FAILURE_KIND.NETWORK) {
      record.nextAttemptAt = Date.now() + NETWORK_RETRY_MS;
    } else {
      record.nextAttemptAt = Date.now() + backoffFor(record.attempts);
    }
    try {
      await putPendingPortal(record);
    } catch (innerError) {
      console.log("portal queue persist error - ", innerError);
    }
    await refreshState();
    console.log("portal save failed - ", record.uuid, record.lastError);
    if (failureKind === FAILURE_KIND.LOGIN) onLoginExpired(tokenUsed);
    return { isSaved: false, failureKind };
  }
};

/**
 * @returns {Promise<{isUploaded: boolean, failureKind: string|null}>}
 */
const uploadOne = async record => {
  const { uuid } = record;
  record.attempts = (record.attempts || 0) + 1;
  record.status = PHOTO_UPLOAD_STATUS.UPLOADING;
  await putPendingPhoto(record);
  await refreshState();
  // The token this request is sent with, to tell an expired login apart from
  // a login that was renewed in the meantime.
  const tokenUsed = ApiService.getToken();
  markProgress();

  try {
    ApiService.initialize();

    if (!record.serverPhotoId) {
      const formData = new FormData();
      formData.append("image", recordToFile(record));
      // The stall timer restarts every time some bytes are sent, so a slow
      // upload keeps going and only a dead connection is stopped. After the
      // last byte it also covers the wait for the server answer.
      const cancelSource = Vue.axios.CancelToken.source();
      let stallTimerId = null;
      const restartStallTimer = () => {
        markProgress();
        clearTimeout(stallTimerId);
        stallTimerId = setTimeout(
          () => cancelSource.cancel("timeout"),
          UPLOAD_STALL_TIMEOUT_MS
        );
      };
      restartStallTimer();
      let response = null;
      try {
        ({ data: response } = await Vue.axios.post(`upload/images`, formData, {
          headers: { "content-type": `multipart/form-data` },
          cancelToken: cancelSource.token,
          onUploadProgress: restartStallTimer
        }));
      } finally {
        clearTimeout(stallTimerId);
      }
      const photoId = response && response.data ? response.data.id : null;
      if (!photoId) throw new Error("upload response has no photo id");
      record.serverPhotoId = photoId;
      // Persist before saving the answer: if the save call fails we retry the
      // save only, we never upload the same image twice.
      await putPendingPhoto(record);
      markProgress();
    }

    const deleteIds = Array.isArray(record.body?.offlineDeleteAttachmentIds)
      ? record.body.offlineDeleteAttachmentIds
      : [];
    if (deleteIds.length > 0) {
      for (const delId of deleteIds) {
        await Vue.axios.post(
          `worksession/deleteAttachmentItem`,
          {
            ...buildDeleteBody(record),
            worksessionSurveyResponseAttachmentId: delId
          },
          { timeout: SAVE_TIMEOUT_MS }
        );
        markProgress();
      }
      delete record.body.offlineDeleteAttachmentIds;
      await putPendingPhoto(record);
    }

    const body = {
      ...record.body,
      // File objects cannot travel as JSON, and the backend ignores saveValue
      // for photo questions because it reads uploadedPhotos instead.
      saveValue: null,
      uploadedPhotos: [{ photoId: record.serverPhotoId }],
      // The survey completion in the body is from when the photo was taken,
      // the flag tells the backend not to overwrite a newer one.
      isQueuedPhotoUpload: true
    };
    const { data: response } = await Vue.axios.post(
      `worksession/realTimeSaveSurveyResponse`,
      body,
      { timeout: SAVE_TIMEOUT_MS }
    );

    markProgress();
    await removePendingPhoto(uuid);
    await refreshState();
    emitSynced({ record, response });
    return { isUploaded: true, failureKind: null };
  } catch (error) {
    markProgress();
    const failureKind = failureKindOf(error);
    // No connection or an expired login is not the photo's fault, so it does
    // not use up an attempt.
    if (failureKind !== FAILURE_KIND.PHOTO) {
      record.attempts = Math.max(0, record.attempts - 1);
    }
    const isOutOfAttempts = record.attempts >= MAX_ATTEMPTS;
    record.status = isOutOfAttempts
      ? PHOTO_UPLOAD_STATUS.FAILED
      : PHOTO_UPLOAD_STATUS.PENDING;
    record.lastError = describeError(error);
    // A failed photo is never dropped, it is tried again on a long delay.
    if (failureKind === FAILURE_KIND.LOGIN) {
      // Sent again right after the next login.
      record.nextAttemptAt = 0;
    } else if (failureKind === FAILURE_KIND.NETWORK) {
      record.nextAttemptAt = Date.now() + NETWORK_RETRY_MS;
    } else if (isOutOfAttempts) {
      record.nextAttemptAt = Date.now() + FAILED_RETRY_MS;
    } else {
      record.nextAttemptAt = Date.now() + backoffFor(record.attempts);
    }
    try {
      await putPendingPhoto(record);
    } catch (innerError) {
      console.log("photo queue persist error - ", innerError);
    }
    await refreshState();
    console.log("photo upload failed - ", record.uuid, record.lastError);
    if (failureKind === FAILURE_KIND.LOGIN) onLoginExpired(tokenUsed);
    return { isUploaded: false, failureKind };
  }
};

/**
 * [Added by E2logy]: one pass over the queue, run while holding the lock.
 * Photos first, then the GPV ratings. Stops early when the connection is lost
 * or the login expired, and when the watchdog released this run.
 */
const runQueuePass = async run => {
  // Records of another user wait for that user, so a rating is never saved
  // under the wrong name on a shared tablet. Records queued by an older build
  // have no owner and are sent by whoever is logged in.
  const userId = store.state.auth.user?.id;
  const isOtherUserRecord = record =>
    !!record.userId && String(record.userId) !== String(userId);
  // After a network error the next items would only wait on the same
  // timeouts, and after a 401 they would be refused too.
  const shouldStopAfter = failureKind =>
    failureKind === FAILURE_KIND.NETWORK ||
    failureKind === FAILURE_KIND.LOGIN ||
    isOfflineNow();

  const records = await getAllPendingPhotos();
  const now = Date.now();
  for (const listedRecord of records) {
    if (!run.isActive || !isLoggedIn()) return;
    // Read the record again, the list may be old by the time its turn comes.
    // A photo already sent is gone, and one with a server photo id only
    // needs its answer saved, not a second image upload.
    const record = await getPendingPhoto(listedRecord.uuid);
    if (!record || busyUuids.has(record.uuid)) continue;
    if (isOtherUserRecord(record)) continue;
    // Failed records are retried too, on the long FAILED_RETRY_MS delay.
    if ((record.nextAttemptAt || 0) > now) continue;
    await waitForBatchSlot(run);
    if (!run.isActive) return;
    const result = await sendOnce(record.uuid, () => uploadOne(record));
    if (!result.isUploaded && shouldStopAfter(result.failureKind)) return;
  }

  // Second pass: the GPV ratings. A rating is only sent once the photo of
  // the same question has reached the server, because savephotosportal
  // attaches it to that answer.
  const remainingPhotos = await getAllPendingPhotos();
  const questionsWithPendingPhoto = new Set(
    remainingPhotos.map(recordQuestionKey)
  );
  const portals = await getAllPendingPortals();
  const portalNow = Date.now();
  for (const listedRecord of portals) {
    if (!run.isActive || !isLoggedIn()) return;
    const record = await getPendingPortal(listedRecord.uuid);
    if (!record || busyUuids.has(record.uuid)) continue;
    if (isOtherUserRecord(record)) continue;
    if ((record.nextAttemptAt || 0) > portalNow) continue;
    if (questionsWithPendingPhoto.has(recordQuestionKey(record))) continue;
    await waitForBatchSlot(run);
    if (!run.isActive) return;
    const result = await sendOnce(record.uuid, () => uploadOnePortal(record));
    if (!result.isSaved && shouldStopAfter(result.failureKind)) return;
  }
};

// [Added by E2logy]: a page closed in the middle of an upload leaves its
// record marked as uploading. Nothing is sending it any more, so it goes back
// to pending. Only done when no other tab holds the queue.
const resetInterruptedUploads = () =>
  withQueueLock(
    async hasLock => {
      if (!hasLock) return;
      const records = await getAllPendingPhotos();
      for (const record of records) {
        if (record.status !== PHOTO_UPLOAD_STATUS.UPLOADING) continue;
        if (busyUuids.has(record.uuid)) continue;
        record.status = PHOTO_UPLOAD_STATUS.PENDING;
        await putPendingPhoto(record);
      }
      const portals = await getAllPendingPortals();
      for (const record of portals) {
        if (record.status !== PHOTO_UPLOAD_STATUS.UPLOADING) continue;
        if (busyUuids.has(record.uuid)) continue;
        record.status = PHOTO_UPLOAD_STATUS.PENDING;
        await putPendingPortal(record);
      }
    },
    { ifAvailable: true }
  );

const PhotoQueueService = {
  state,

  get pendingCount() {
    return state.pendingCount;
  },

  get failedCount() {
    return state.failedCount;
  },

  /**
   * Stores a photo and its survey answer payload locally, then triggers the
   * background uploader. Resolves once the photo is safely on disk.
   * @param {Object} details names shown on the pending photos screen (POS,
   * survey, question...), saved as they are when the photo is taken
   * @returns {Promise<string|null>} the queue uuid, or null when it could not be stored
   */
  async enqueue({ file, body, meta = {}, details = null }) {
    if (!file) return null;
    const uuid = createUuid();
    const record = {
      uuid,
      createdAt: Date.now(),
      blob: file,
      fileName: file.name || "photo.jpg",
      mimeType: file.type || "image/jpeg",
      size: file.size || null,
      userId: store.state.auth.user?.id || null,
      details,
      worksessionPosBrandId: meta.worksessionPosBrandId || null,
      surveyId: meta.surveyId || null,
      componentId: meta.componentId || null,
      surveyProOrMatId: meta.surveyProOrMatId || null,
      surveyQuestionId: meta.surveyQuestionId || null,
      questionType: meta.questionType || null,
      photoType: meta.photoType || null,
      body,
      status: PHOTO_UPLOAD_STATUS.PENDING,
      attempts: 0,
      nextAttemptAt: 0,
      lastError: null,
      serverPhotoId: null
    };
    try {
      await putPendingPhoto(record);
    } catch (error) {
      console.log("photo queue enqueue error - ", error);
      return null;
    }
    await refreshState();
    // Do not await: the surveyor continues while this runs.
    PhotoQueueService.drain();
    return uuid;
  },

  /**
   * Uploads every due record, one at a time. Safe to call as often as you like,
   * a call while a drain is running gets the promise of that same drain.
   * @param {Object} options waitForLock: wait while another tab is sending,
   * instead of leaving the work to that tab
   */
  drain(options = {}) {
    if (isDraining) return drainPromise;
    // [Added by E2logy]: without a login every request would be refused. The
    // queue starts again after the next login (see App.vue).
    if (!isLoggedIn()) return Promise.resolve();
    isDraining = true;
    drainPromise = (async () => {
      try {
        await withQueueLock(
          async hasLock => {
            // Another tab is sending right now, and it sends these records too.
            if (!hasLock) return;
            // [Modified by E2logy]: while "Send all" runs, every run sends in
            // batches, also one started by the timer in the meantime.
            const run = createRun({ isBatched: !!flushPromise });
            activeRun = run;
            try {
              // The lock is held until the pass ends, or until the watchdog
              // releases a pass that is stuck on a call that never answers.
              await Promise.race([runQueuePass(run), run.released]);
            } finally {
              run.isActive = false;
              if (activeRun === run) activeRun = null;
            }
          },
          { ifAvailable: !options.waitForLock }
        );
      } catch (error) {
        console.log("photo queue drain error - ", error);
      } finally {
        isDraining = false;
        drainPromise = null;
        await refreshState();
      }
    })();
    return drainPromise;
  },

  /**
   * "Send now": tries every photo and rating on the device, failed ones
   * included, and keeps going while it makes progress. [Modified by E2logy]:
   * sends BATCH_SIZE items in a row, then pauses BATCH_PAUSE_MS.
   * @returns {Promise<{sent: number, remaining: number}>}
   */
  flushAll() {
    if (flushPromise) return flushPromise;
    flushPromise = (async () => {
      try {
        // The reset waits for the lock. Otherwise it could write back a photo
        // that a drain had just sent, and that photo would be uploaded again.
        await withQueueLock(async () => {
          const records = await getAllPendingPhotos();
          for (const record of records) {
            record.status = PHOTO_UPLOAD_STATUS.PENDING;
            record.attempts = 0;
            record.nextAttemptAt = 0;
            await putPendingPhoto(record);
          }
          const portals = await getAllPendingPortals();
          for (const record of portals) {
            record.nextAttemptAt = 0;
            await putPendingPortal(record);
          }
        });
        await refreshState();
        const total = state.pendingCount + state.pendingPortalCount;
        state.flush = { isRunning: true, total, sent: 0 };

        let remaining = total;
        while (remaining > 0) {
          // A drain started by the timer may still be finishing, so wait for
          // it and then run one more pass with the reset records. That pass
          // waits if another tab is sending, instead of skipping.
          if (drainPromise) await drainPromise;
          await PhotoQueueService.drain({ waitForLock: true });
          const nextRemaining = state.pendingCount + state.pendingPortalCount;
          // Nothing more went out: no connection or the server refuses. The
          // records stay on the device and the worker keeps trying.
          if (nextRemaining >= remaining) break;
          remaining = nextRemaining;
        }
        remaining = state.pendingCount + state.pendingPortalCount;
        return { sent: Math.max(0, total - remaining), remaining };
      } catch (error) {
        console.log("photo queue flush error - ", error);
        return {
          sent: 0,
          remaining: state.pendingCount + state.pendingPortalCount
        };
      } finally {
        state.flush = { ...state.flush, isRunning: false };
        flushPromise = null;
      }
    })();
    return flushPromise;
  },

  async retry(uuid) {
    try {
      // Same lock as the drain, so a photo sent in the meantime is not
      // written back and uploaded again.
      await withQueueLock(async () => {
        const record = await getPendingPhoto(uuid);
        if (!record) return;
        record.status = PHOTO_UPLOAD_STATUS.PENDING;
        record.attempts = 0;
        record.nextAttemptAt = 0;
        record.lastError = null;
        await putPendingPhoto(record);
      });
      await refreshState();
      PhotoQueueService.drain();
    } catch (error) {
      console.log("photo queue retry error - ", error);
    }
  },

  async retryAll() {
    try {
      await withQueueLock(async () => {
        const records = await getAllPendingPhotos();
        for (const record of records) {
          if (record.status !== PHOTO_UPLOAD_STATUS.FAILED) continue;
          record.status = PHOTO_UPLOAD_STATUS.PENDING;
          record.attempts = 0;
          record.nextAttemptAt = 0;
          record.lastError = null;
          await putPendingPhoto(record);
        }
      });
      await refreshState();
      PhotoQueueService.drain();
    } catch (error) {
      console.log("photo queue retryAll error - ", error);
    }
  },

  statusOf(uuid) {
    if (!uuid) return null;
    return state.statuses[uuid] || null;
  },

  /**
   * Queued photos of one survey question, oldest first. Reactive, so a
   * component can render an upload status without any extra props.
   */
  entriesForQuestion(questionIdentity) {
    const key = buildQuestionKey(questionIdentity || {});
    return state.byQuestion[key] || [];
  },
  
  hasPendingPhotoForQuestionKey(key) {
    return questionHasPendingPhoto(key);
  },

  /**
   * Queues the GPV rating and comments of a photo question. Resolves once it is
   * safely on the device.
   */
  async enqueuePortal({ body, meta = {} }) {
    if (!body) return null;
    const uuid = createUuid();
    const record = {
      uuid,
      createdAt: Date.now(),
      worksessionPosBrandId: meta.worksessionPosBrandId || null,
      surveyId: meta.surveyId || null,
      componentId: meta.componentId || null,
      surveyProOrMatId: meta.surveyProOrMatId || null,
      surveyQuestionId: meta.surveyQuestionId || null,
      body,
      status: PHOTO_UPLOAD_STATUS.PENDING,
      attempts: 0,
      nextAttemptAt: 0,
      lastError: null
    };
    try {
      await putPendingPortal(record);
    } catch (error) {
      console.log("portal queue enqueue error - ", error);
      return null;
    }
    await refreshState();
    PhotoQueueService.drain();
    return uuid;
  },

  // Queued ratings of one survey question. Reactive.
  portalEntriesForQuestion(questionIdentity) {
    const key = buildQuestionKey(questionIdentity || {});
    return state.portalByQuestion[key] || [];
  },

  async listAllPortals() {
    try {
      return await getAllPendingPortals();
    } catch (error) {
      console.log("portal queue list error - ", error);
      return [];
    }
  },

  async listPortalsByWorksessionPosBrand(worksessionPosBrandId) {
    if (!worksessionPosBrandId) return [];
    const records = await PhotoQueueService.listAllPortals();
    return records.filter(
      el =>
        String(el.worksessionPosBrandId) === String(worksessionPosBrandId) ||
        String(el.body?.worksessionPosBrandId) === String(worksessionPosBrandId)
    );
  },

  onPortalSynced(listener) {
    if (typeof listener === "function") portalSyncedListeners.add(listener);
  },

  offPortalSynced(listener) {
    portalSyncedListeners.delete(listener);
  },

  async listAll() {
    try {
      return await getAllPendingPhotos();
    } catch (error) {
      console.log("photo queue list error - ", error);
      return [];
    }
  },

  async listByWorksessionPosBrand(worksessionPosBrandId) {
    if (!worksessionPosBrandId) return [];
    const records = await PhotoQueueService.listAll();
    return records.filter(
      el =>
        String(el.worksessionPosBrandId) === String(worksessionPosBrandId) ||
        String(el.body?.worksessionPosBrandId) === String(worksessionPosBrandId)
    );
  },

  // Photos plus ratings, so nothing is closed while something is still local.
  async countPending() {
    await refreshState();
    return state.pendingCount + state.pendingPortalCount;
  },

  onSynced(listener) {
    if (typeof listener === "function") syncedListeners.add(listener);
  },

  offSynced(listener) {
    syncedListeners.delete(listener);
  },

  start() {
    if (isStarted) return;
    isStarted = true;
    // Asks the browser not to clear IndexedDB when the tablet is low on space,
    // the photos waiting to be sent only exist there.
    if (navigator.storage && typeof navigator.storage.persist === "function") {
      navigator.storage.persist().catch(() => {});
    }
    resetInterruptedUploads()
      .catch(error => console.log("photo queue reset error - ", error))
      .then(() => refreshState())
      .then(() => PhotoQueueService.drain());
    if (!drainIntervalId) {
      drainIntervalId = setInterval(onQueueTick, DRAIN_INTERVAL_MS);
    }
    // [Modified by E2logy]: also wakes up when the tablet comes back from sleep
    // or the user returns to the app, not only when the network comes back.
    window.addEventListener("online", onQueueTick);
    window.addEventListener("pageshow", onQueueTick);
    document.addEventListener("visibilitychange", onVisibilityChange);
  },

  stop() {
    isStarted = false;
    if (drainIntervalId) {
      clearInterval(drainIntervalId);
      drainIntervalId = null;
    }
    window.removeEventListener("online", onQueueTick);
    window.removeEventListener("pageshow", onQueueTick);
    document.removeEventListener("visibilitychange", onVisibilityChange);
  }
};

// [Added by E2logy]: every wake up first lets the watchdog release a stuck
// run, then starts a run if none is going.
function onQueueTick() {
  releaseStalledRun();
  PhotoQueueService.drain();
}

function onVisibilityChange() {
  if (document.visibilityState === "visible") onQueueTick();
}

export default PhotoQueueService;
